import { IonItem, IonList } from '@ionic/react';
import { useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { Checkbox, STYLE_TYPE } from 'baseui/checkbox';
import { DURATION, useSnackbar } from 'baseui/snackbar';
import { Check } from 'baseui/icon';
import { ParagraphMedium, ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadUserSolutions, setSolutionPreview } from '../application/actions/solutions';
import { getUserSolutions } from '../application/selectors/solutions';
import useLocale from '../hooks/useLocale';
import ReDoBackButton from './ReDoBackButton';


// ReDoPublicSolutionsSettings lists the signed-in user's own solutions with a
// public/private toggle. Flipping a toggle opts that solution in/out of the
// logged-out public landing feed (owner-gated PUT /project/solution/{id}/preview).
const ReDoPublicSolutionsSettings = ({ userId, onBack }) => {
  const dispatch = useDispatch();
  const [css, theme] = useStyletron();
  const { t } = useLocale();
  const { enqueue } = useSnackbar();

  const solutions = useSelector(getUserSolutions);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState(() => new Set());

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.resolve(dispatch(loadUserSolutions(userId))).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [dispatch, userId]);

  useEffect(() => {
    setItems(solutions || []);
  }, [solutions]);

  const onToggle = (solution, checked) => {
    if (savingIds.has(solution.id)) return; // ignore taps while a save is in flight
    setSavingIds(prev => { const next = new Set(prev); next.add(solution.id); return next; });
    // optimistic update
    setItems(prev => prev.map(s => s.id === solution.id ? { ...s, publicPreview: checked } : s));
    Promise.resolve(dispatch(setSolutionPreview({ solutionId: solution.id, publicPreview: checked })))
      .then(() => {
        enqueue({
          message: checked ? t('settings.public_solutions_now_public') : t('settings.public_solutions_now_private'),
          dismissAfter: DURATION.short,
          startEnhancer: () => <Check size={theme.sizing.scale800} />,
        });
      })
      .catch(() => {
        // revert on failure
        setItems(prev => prev.map(s => s.id === solution.id ? { ...s, publicPreview: !checked } : s));
        enqueue({ message: t('settings.public_solutions_error'), dismissAfter: DURATION.short });
      })
      .finally(() => {
        setSavingIds(prev => { const next = new Set(prev); next.delete(solution.id); return next; });
      });
  };

  return (
    <>
      <ReDoBackButton onClick={onBack} />
      <Block marginBottom='4ch'>
        <ParagraphMedium overrides={{ Block: { style: { fontWeight: '600' } } }}>{t('settings.public_solutions_heading')}</ParagraphMedium>
        <ParagraphSmall color={theme.colors.primary400} marginTop='0'>{t('settings.public_solutions_description')}</ParagraphSmall>

        {!loading && !items.length &&
          <ParagraphSmall color={theme.colors.primary400}>{t('settings.public_solutions_empty')}</ParagraphSmall>
        }

        {!!items.length &&
          <IonList inset className={css({ margin: '1ch 0 !important' })}>
            {items.map((solution, index) => (
              <div key={solution.id}>
                {index !== 0 &&
                  <Block backgroundColor='white' display='flex' justifyContent='center'>
                    <Block height='1px' backgroundColor={theme.colors.secondary200} width='96%' />
                  </Block>
                }
                <IonItem lines='none' className={css({ '::part(native)': { background: 'white', paddingTop: '.5ch', paddingBottom: '.5ch' } })}>
                  <Block alignItems='center' paddingRight='1ch' width='100%' display='flex' justifyContent='space-between' gridGap='2ch'>
                    <ParagraphSmall margin='0' overrides={{ Block: { style: { fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } }}>
                      {solution.title}
                    </ParagraphSmall>
                    <Checkbox
                      checked={!!solution.publicPreview}
                      disabled={savingIds.has(solution.id)}
                      checkmarkType={STYLE_TYPE.toggle_round}
                      onChange={(e) => onToggle(solution, e.currentTarget.checked)}
                      overrides={{ Input: { props: { 'aria-label': `${t('settings.public_solutions_heading')}: ${solution.title}` } } }}
                    />
                  </Block>
                </IonItem>
              </div>
            ))}
          </IonList>
        }
      </Block>
    </>
  );
};

export default ReDoPublicSolutionsSettings;
